// Adapters wiring the CH6c driver (linking.ts) to the real web pieces: the CH6a
// relay client, and this device's chat store as the history source (Primary) /
// sink (New device). Kept apart from the driver so the driver stays pure and
// unit-testable with fakes.

import { sync } from './chatClient';
import { chatDb, type ConvMeta } from './chatDb';
import { linkingApi, type LinkSide } from './linkingApi';
import type { HistorySink, HistorySource, LinkRelay } from './linking';
import type { LinkConvMeta } from './linkWire';

/** Bind the relay client to one rendezvous + side. */
export function relayFor(rendezvousId: string, side: LinkSide): LinkRelay {
  return {
    send: (frame) => linkingApi.send(rendezvousId, side, frame).then(() => undefined),
    recv: () => linkingApi.recv(rendezvousId, side),
  };
}

const maxSeq = (msgs: { seq: number }[]): number => msgs.reduce((m, x) => Math.max(m, x.seq), 0);

/** The Primary's read side: every joined conversation that actually has history. */
export function historySource(): HistorySource {
  return {
    listConvs: async () => {
      const out: LinkConvMeta[] = [];
      for (const c of await chatDb.allConvs()) {
        if (!c.joined || c.removed) continue;
        const msgs = await chatDb.messagesOf(c.id);
        // A conversation I opened but that never carried a message is invisible
        // to the peer (initiatedByMe); nothing to hand over.
        if (msgs.length === 0 && c.initiatedByMe) continue;
        out.push({
          id: c.id,
          kind: c.kind,
          peerUserId: c.peerUserId,
          initiatedByMe: c.initiatedByMe,
          maxSeq: maxSeq(msgs),
        });
      }
      return out;
    },
    messagesAfter: async (convId, afterSeq) =>
      (await chatDb.messagesOf(convId)).filter((m) => m.seq > afterSeq),
  };
}

/** The New device's write side. Message history is written straight in; the
 * conversation's device-local MLS state is NEVER touched — only display fields
 * are filled, and only when this device has no membership row yet. */
export function historySink(): HistorySink {
  return {
    cursor: async () => {
      const cur: Record<string, number> = {};
      for (const c of await chatDb.allConvs()) {
        const msgs = await chatDb.messagesOf(c.id);
        if (msgs.length) cur[c.id] = maxSeq(msgs);
      }
      return cur;
    },
    applyConv: async (meta) => {
      const existing = await chatDb.getConv(meta.id);
      if (existing) {
        await chatDb.putConv({
          ...existing,
          peerUserId: existing.peerUserId ?? meta.peerUserId,
          initiatedByMe: existing.initiatedByMe ?? meta.initiatedByMe,
        });
        return;
      }
      // No membership yet (reconcile hasn't reached this conversation) — a
      // display-only placeholder; joined:false keeps sending off until reconcile
      // completes it, and the transferred messages still render.
      const placeholder: ConvMeta = {
        id: meta.id,
        kind: meta.kind,
        peerUserId: meta.peerUserId,
        joined: false,
        joinSeq: 0,
        epoch: 0,
        lastProcessedSeq: 0,
        lastReadSeq: 0,
        initiatedByMe: meta.initiatedByMe,
      };
      await chatDb.putConv(placeholder);
    },
    applyMessages: async (convId, messages) => {
      for (const m of messages) await chatDb.putMessage(m);
    },
  };
}

/** Republish Discussions from the local store once a transfer lands. */
export function refreshAfterTransfer(): Promise<void> {
  return sync();
}
